/**
 * Application Insights Service (Healthcare Portal parity + React plugin)
 *
 * Initializes Azure Application Insights for Form Engine Portal using
 * `@microsoft/applicationinsights-web` and `@microsoft/applicationinsights-react-js`.
 *
 * React Router v6+: `ReactPlugin` is registered as an SDK extension together with
 * `enableAutoRouteTracking: true` (Microsoft’s recommended pattern when router
 * history is not passed explicitly — see applicationinsights-react-js README).
 *
 * Setup:
 * 1. Dependencies: `@microsoft/applicationinsights-web`, `@microsoft/applicationinsights-react-js`
 * 2. Production: set `VITE_APPLICATIONINSIGHTS_CONNECTION_STRING` (preferred) or legacy `VITE_APPINSIGHTS_INSTRUMENTATION_KEY`
 * 3. `main.tsx` calls `initializeApplicationInsights()` when `import.meta.env.PROD`
 */

import type { ReactPlugin } from '@microsoft/applicationinsights-react-js';

type ApplicationInsights = {
  loadAppInsights: () => void;
  trackPageView: (pageView?: {
    name?: string;
    uri?: string;
    properties?: Record<string, string>;
    measurements?: Record<string, number>;
  }) => void;
  trackEvent: (event: {
    name: string;
    properties?: Record<string, string>;
    measurements?: Record<string, number>;
  }) => void;
  trackMetric: (
    metric: { name: string; average: number; sampleCount?: number; min?: number; max?: number },
    properties?: Record<string, string>
  ) => void;
  trackException: (exception: {
    exception: Error;
    severityLevel?: number;
    properties?: Record<string, string>;
    measurements?: Record<string, number>;
  }) => void;
  trackTrace: (trace: { message: string; severityLevel?: number; properties?: Record<string, string> }) => void;
  setAuthenticatedUserContext: (userId: string, accountId?: string, storeInCookie?: boolean) => void;
  clearAuthenticatedUserContext: () => void;
};

let appInsights: ApplicationInsights | null = null;
/** Same instance passed to `ApplicationInsights` config `extensions` — for `withAITracking` if needed later. */
let reactPlugin: ReactPlugin | null = null;
let isInitialized = false;

type AppInsightsAuthConfig =
  | { connectionString: string }
  | { instrumentationKey: string };

function resolveAppInsightsAuthConfig(legacyKeyOverride?: string): AppInsightsAuthConfig | null {
  const connectionString = import.meta.env.VITE_APPLICATIONINSIGHTS_CONNECTION_STRING?.trim();
  if (connectionString) {
    return { connectionString };
  }

  const instrumentationKey =
    legacyKeyOverride?.trim() || import.meta.env.VITE_APPINSIGHTS_INSTRUMENTATION_KEY?.trim();
  if (instrumentationKey) {
    return { instrumentationKey };
  }

  return null;
}

/**
 * Initialize Application Insights. Call once at startup (`main.tsx`).
 * No-ops in development (same as Healthcare Portal).
 */
export function initializeApplicationInsights(instrumentationKey?: string): ApplicationInsights | null {
  if (isInitialized) {
    return appInsights;
  }

  if (import.meta.env.DEV) {
    console.log('[ApplicationInsights] Skipping initialization in development mode');
    isInitialized = true;
    return null;
  }

  const authConfig = resolveAppInsightsAuthConfig(instrumentationKey);

  if (!authConfig) {
    console.warn(
      '[ApplicationInsights] Connection string or instrumentation key not provided. ' +
        'Set VITE_APPLICATIONINSIGHTS_CONNECTION_STRING (preferred) or VITE_APPINSIGHTS_INSTRUMENTATION_KEY.'
    );
    isInitialized = true;
    return null;
  }

  try {
    Promise.all([
      import('@microsoft/applicationinsights-web'),
      import('@microsoft/applicationinsights-react-js'),
    ])
      .then(([webModule, reactModule]) => {
        const { ApplicationInsights: AI } = webModule as {
          ApplicationInsights: new (cfg: { config: Record<string, unknown> }) => ApplicationInsights;
        };
        const { ReactPlugin: ReactPluginCtor } = reactModule as {
          ReactPlugin: new () => ReactPlugin;
        };

        reactPlugin = new ReactPluginCtor();

        appInsights = new AI({
          config: {
            ...authConfig,
            enableAutoRouteTracking: true,
            enableAutoExceptionTracking: true,
            enableAutoDependencyTracking: true,
            enableAutoPerformanceTracking: true,
            disableExceptionTracking: false,
            samplingPercentage: 100,
            enableCorsCorrelation: true,
            enableRequestHeaderTracking: true,
            enableResponseHeaderTracking: true,
            extensions: [reactPlugin],
          },
        });

        appInsights.loadAppInsights();
        appInsights.trackPageView();

        console.log('[ApplicationInsights] Initialized successfully (with ReactPlugin for React Router)');
        isInitialized = true;
      })
      .catch((error: unknown) => {
        console.error('[ApplicationInsights] Failed to load Application Insights modules:', error);
        isInitialized = true;
      });
  } catch (error) {
    console.error('[ApplicationInsights] Failed to initialize:', error);
    isInitialized = true;
    return null;
  }

  return appInsights;
}

/** React plugin instance after successful init; use with `withAITracking` from `@microsoft/applicationinsights-react-js` if needed. */
export function getReactPlugin(): ReactPlugin | null {
  return reactPlugin;
}

export function getApplicationInsights(): ApplicationInsights | null {
  return appInsights;
}

export function isApplicationInsightsAvailable(): boolean {
  return appInsights !== null && isInitialized;
}

export function trackEvent(
  name: string,
  properties?: Record<string, string>,
  measurements?: Record<string, number>
): void {
  if (appInsights) {
    appInsights.trackEvent({ name, properties, measurements });
  }
}

export function trackMetric(
  name: string,
  value: number,
  count?: number,
  min?: number,
  max?: number,
  properties?: Record<string, string>
): void {
  if (appInsights) {
    appInsights.trackMetric({ name, average: value, sampleCount: count || 1, min, max }, properties);
  }
}

export function trackException(
  exception: Error,
  severityLevel?: number,
  properties?: Record<string, string>,
  measurements?: Record<string, number>
): void {
  if (appInsights) {
    appInsights.trackException({
      exception,
      severityLevel,
      properties,
      measurements,
    });
  }
}

export function trackPageView(
  name?: string,
  uri?: string,
  properties?: Record<string, string>,
  measurements?: Record<string, number>
): void {
  if (appInsights) {
    appInsights.trackPageView({ name, uri, properties, measurements });
  }
}

export function trackTrace(
  message: string,
  severityLevel?: number,
  properties?: Record<string, string>
): void {
  if (appInsights) {
    appInsights.trackTrace({ message, severityLevel, properties });
  }
}

export function setAuthenticatedUserContext(userId: string, accountId?: string): void {
  if (appInsights) {
    appInsights.setAuthenticatedUserContext(userId, accountId, true);
  }
}

export function clearAuthenticatedUserContext(): void {
  if (appInsights) {
    appInsights.clearAuthenticatedUserContext();
  }
}
